import { useMemo, useState } from "react";
import type { V10ProjectListItem } from "../adapters/v10-fixtures.js";
import { V10_PROJECT_GROUP_LABELS } from "../adapters/v10-fixtures.js";
import { v10NewWorkSheet, v10ProjectRoute } from "../adapters/v10-button-endpoints.js";
import {
  applyV10PipelineFilters,
  V10_PIPELINE_FILTER_DEFAULTS,
  type V10PipelineFilterState,
} from "../adapters/v10-pipeline-filters.js";
import { humanV10QueueRiskLabel } from "../adapters/v10-queue-risk.js";
import { V10Btn, V10Kicker, V10Pill } from "../components/V10Primitives.js";
import type { V10Navigate } from "../routing/v10-routing.js";

const GROUP_ORDER: readonly V10ProjectListItem["group"][] = [
  "draft",
  "active",
  "waiting-client",
  "blocked",
  "handoff-ready",
  "live-care",
];

function PipelineFilters({
  filters,
  onChange,
}: {
  readonly filters: V10PipelineFilterState;
  readonly onChange: (next: V10PipelineFilterState) => void;
}) {
  return (
    <div className="v10-pipeline-filters" aria-label="Project pipeline filters" data-testid="v10-pipeline-filters">
      <label>
        Stage
        <select
          value={filters.stage}
          onChange={(e) => onChange({ ...filters, stage: e.target.value as V10PipelineFilterState["stage"] })}
        >
          <option value="all">All stages</option>
          {GROUP_ORDER.map((group) => (
            <option key={group} value={group}>
              {V10_PROJECT_GROUP_LABELS[group]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Due
        <select
          value={filters.due}
          onChange={(e) => onChange({ ...filters, due: e.target.value as V10PipelineFilterState["due"] })}
        >
          <option value="all">All due states</option>
          <option value="on_track">On track</option>
          <option value="waiting_client">Waiting on client</option>
          <option value="blocked">Blocked / overdue</option>
        </select>
      </label>
      <label>
        Risk
        <select
          value={filters.risk}
          onChange={(e) => onChange({ ...filters, risk: e.target.value as V10PipelineFilterState["risk"] })}
        >
          <option value="all">All risk</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </label>
      <label>
        Client action
        <select
          value={filters.clientAction}
          onChange={(e) =>
            onChange({ ...filters, clientAction: e.target.value as V10PipelineFilterState["clientAction"] })
          }
        >
          <option value="all">All</option>
          <option value="outstanding">Outstanding client action</option>
          <option value="none">No outstanding client action</option>
        </select>
      </label>
    </div>
  );
}

export function V10ProjectsView({
  onNavigate,
  onOpenProject,
  projects,
  loading,
  error,
}: {
  readonly onNavigate: V10Navigate;
  readonly onOpenProject: V10Navigate;
  readonly projects: readonly V10ProjectListItem[];
  readonly loading: boolean;
  readonly error: string | null;
}) {
  const [filters, setFilters] = useState<V10PipelineFilterState>(V10_PIPELINE_FILTER_DEFAULTS);
  const filteredProjects = useMemo(() => applyV10PipelineFilters(projects, filters), [filters, projects]);

  return (
    <section className="v10-screen" aria-label="Projects">
      <div className="v10-section-head v10-section-head--flush">
        <div>
          <V10Kicker>Projects</V10Kicker>
          <h2>Every client opens into one Project Control Room.</h2>
        </div>
        <V10Btn variant="primary" onClick={() => onNavigate(v10NewWorkSheet("DEFAULT_GENERATION"))}>
          + New project
        </V10Btn>
      </div>
      <PipelineFilters filters={filters} onChange={setFilters} />
      {error ? <p className="v10-validation">{error}</p> : null}
      {loading && projects.length === 0 ? <p className="v10-muted">Loading projects…</p> : null}
      {!loading && projects.length === 0 ? (
        <p className="v10-muted">No projects yet — create one to get started.</p>
      ) : null}
      {!loading && projects.length > 0 && filteredProjects.length === 0 ? (
        <p className="v10-muted">No projects match the current filters.</p>
      ) : null}
      {GROUP_ORDER.map((group) => {
        const items = filteredProjects.filter((p) => p.group === group);
        if (items.length === 0) return null;
        return (
          <section key={group} className="v10-project-group" aria-label={V10_PROJECT_GROUP_LABELS[group]}>
            <h3>{V10_PROJECT_GROUP_LABELS[group]}</h3>
            <div className="v10-grid-two">
              {items.map((project) => (
                <article key={project.id} className="v10-card v10-card--major v10-project-card">
                  <div>
                    <h4>{project.name}</h4>
                    <p className="v10-muted">{project.summary}</p>
                    <dl className="v10-pipeline-meta" data-testid="v10-pipeline-row-meta">
                      <div>
                        <dt>Owner</dt>
                        <dd>{project.ownerLabel}</dd>
                      </div>
                      <div>
                        <dt>Due</dt>
                        <dd>{project.dueLabel}</dd>
                      </div>
                      <div>
                        <dt>Risk</dt>
                        <dd>{humanV10QueueRiskLabel(project.riskLevel)}</dd>
                      </div>
                      <div>
                        <dt>Client action</dt>
                        <dd>{project.outstandingClientAction ? "Outstanding" : "None"}</dd>
                      </div>
                    </dl>
                    <div className="v10-pill-row">
                      {project.pills.map((pill) => (
                        <V10Pill key={pill}>{pill}</V10Pill>
                      ))}
                    </div>
                    <span className="v10-age">{project.ageLabel}</span>
                  </div>
                  <V10Btn
                    variant="primary"
                    onClick={() =>
                      onOpenProject(
                        v10ProjectRoute({
                          projectId: project.id,
                          section: project.currentSection,
                          mode: project.mode,
                        }),
                      )
                    }
                  >
                    Open Control Room
                  </V10Btn>
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </section>
  );
}
